import { decodeFileName } from '../../utils/fileNameHelpers';

export interface ArchiveCardCategory {
  _id: string;
  name: string;
  parentCategory?: string | null | { _id?: string };
}

export interface ArchiveCardAsset {
  title?: string;
  fileName?: string;
  originalFileName?: string;
  fileType?: string;
  fileSize?: number;
  width?: number;
  height?: number;
  categories?: Array<string | { _id?: string }>;
  metadata?: Record<string, unknown>;
}

export interface ArchiveCardPresentation {
  title: string;
  metaLine: string;
  categoryNames: string[];
  categoryLabels: Array<{ id: string; name: string }>;
  isRecipe: boolean;
}

const normalizeMetaValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    const first = value.map(item => String(item).trim()).find(Boolean);
    return first || '';
  }
  return String(value).trim();
};

const getCategoryRefId = (categoryRef: unknown): string => {
  if (!categoryRef) return '';
  if (typeof categoryRef === 'string') return categoryRef;
  if (typeof categoryRef === 'object' && categoryRef && '_id' in categoryRef) {
    const id = (categoryRef as { _id?: unknown })._id;
    return id ? String(id) : '';
  }
  return String(categoryRef);
};

const getParentId = (category: ArchiveCardCategory): string | null => {
  const parent = category.parentCategory;
  if (!parent) return null;
  if (typeof parent === 'string') return parent;
  if (typeof parent === 'object' && parent._id) return String(parent._id);
  return null;
};

const findNamedRootId = (categories: ArchiveCardCategory[], name: string): string | null => {
  const normalized = name.trim().toLowerCase();
  const named = categories.filter(category => (category.name || '').trim().toLowerCase() === normalized);
  const root = named.find(category => !getParentId(category));
  return (root || named[0])?._id || null;
};

const isUnderRoot = (
  asset: ArchiveCardAsset,
  rootId: string | null,
  parentById: Map<string, string | null>,
): boolean => {
  if (!rootId || !Array.isArray(asset.categories) || asset.categories.length === 0) return false;

  const isDescendant = (categoryId: string): boolean => {
    let current: string | null | undefined = categoryId;
    const visited = new Set<string>();
    while (current && !visited.has(current)) {
      if (current === rootId) return true;
      visited.add(current);
      current = parentById.get(current) || null;
    }
    return false;
  };

  return asset.categories
    .map(getCategoryRefId)
    .filter(Boolean)
    .some(isDescendant);
};

const metadataValue = (metadata: Record<string, unknown> | undefined, key: string): string => (
  normalizeMetaValue(metadata?.[key])
);

const metadataByKeyMatch = (
  metadata: Record<string, unknown> | undefined,
  matches: (key: string) => boolean,
): string => {
  if (!metadata) return '';
  for (const [key, value] of Object.entries(metadata)) {
    if (!matches(key.toLowerCase())) continue;
    const normalized = normalizeMetaValue(value);
    if (normalized) return normalized;
  }
  return '';
};

const recipeName = (asset: ArchiveCardAsset): string => {
  const direct = metadataValue(asset.metadata, 'receptnamn_recept_id');
  if (direct) return direct;
  return metadataByKeyMatch(asset.metadata, key => key === 'receptnamn_recept_id');
};

const wineName = (asset: ArchiveCardAsset): string => {
  const direct = metadataValue(asset.metadata, 'Produktnamn_vin_id');
  if (direct) return direct;
  return metadataByKeyMatch(asset.metadata, key => key.includes('produktnamn_vin_id'));
};

const productName = (asset: ArchiveCardAsset): string => {
  const direct = metadataValue(asset.metadata, 'produktnamn') || metadataValue(asset.metadata, 'Produktnamn');
  if (direct) return direct;
  return metadataByKeyMatch(
    asset.metadata,
    key => key.includes('produktnamn') && !key.includes('vin_id'),
  );
};

const productMetaLine = (asset: ArchiveCardAsset): string => {
  const artikelnummer = metadataValue(asset.metadata, 'artikelnummer');
  const vikt = metadataValue(asset.metadata, 'vikt_antal_validoo');
  if (artikelnummer && vikt) return `${artikelnummer} | ${vikt}`;
  return artikelnummer || vikt || '';
};

const wineMetaLine = (asset: ArchiveCardAsset): string => {
  const artikelnummer = metadataValue(asset.metadata, 'artikelnummer_vin_id');
  const volym = metadataValue(asset.metadata, 'volym_vin_id');
  if (artikelnummer && volym) return `${artikelnummer} | ${volym}`;
  return artikelnummer || volym || '';
};

export const formatArchiveFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const displayFileName = (asset: ArchiveCardAsset): string => {
  const raw = asset.originalFileName || asset.fileName || '';
  const base = raw.split(/[/\\]/).pop() || raw;
  return base ? decodeFileName(base) : '';
};

const dimensionLine = (asset: ArchiveCardAsset): string => {
  if (!asset.width || !asset.height) return '';
  const size = formatArchiveFileSize(asset.fileSize);
  const dimensions = `${asset.width} × ${asset.height} px`;
  return size ? `${dimensions} | ${size}` : dimensions;
};

export const getArchiveCardPresentation = (
  asset: ArchiveCardAsset,
  categories: ArchiveCardCategory[],
): ArchiveCardPresentation => {
  const parentById = new Map<string, string | null>();
  const categoryById = new Map<string, ArchiveCardCategory>();
  categories.forEach(category => {
    if (!category?._id) return;
    categoryById.set(category._id, category);
    parentById.set(category._id, getParentId(category));
  });

  const productsRootId = findNamedRootId(categories, 'produkter');
  const wineRootId = findNamedRootId(categories, 'vin');
  const recipesRootId = findNamedRootId(categories, 'recept');
  const isRecipe = isUnderRoot(asset, recipesRootId, parentById);
  const isWine = isUnderRoot(asset, wineRootId, parentById);
  const isProduct = isUnderRoot(asset, productsRootId, parentById);

  let title = '';
  if (isRecipe) title = recipeName(asset);
  if (!title && isWine) title = wineName(asset);
  if (!title && isProduct) title = productName(asset);
  if (!title) title = normalizeMetaValue(asset.title);
  if (!title) title = displayFileName(asset);

  const metaLine = isProduct
    ? productMetaLine(asset)
    : isWine
      ? wineMetaLine(asset)
      : dimensionLine(asset);

  const categoryLabels = (asset.categories || [])
    .map(getCategoryRefId)
    .map(id => {
      const name = categoryById.get(id)?.name || '';
      return name ? { id, name } : null;
    })
    .filter((label): label is { id: string; name: string } => Boolean(label));

  return {
    title,
    metaLine,
    categoryNames: categoryLabels.map(label => label.name),
    categoryLabels,
    isRecipe,
  };
};

const parentById = (categories: ArchiveCardCategory[]): Map<string, string | null> => {
  const parents = new Map<string, string | null>();
  categories.forEach(category => {
    if (!category?._id) return;
    parents.set(category._id, getParentId(category));
  });
  return parents;
};

export const listMainCategories = (categories: ArchiveCardCategory[]): ArchiveCardCategory[] => (
  categories.filter(category => category._id && !getParentId(category))
);

export const categoryIdsUnderMain = (
  categories: ArchiveCardCategory[],
  mainCategoryId: string,
): string[] => {
  if (!mainCategoryId) return [];
  const parents = parentById(categories);
  const ids = categories
    .map(category => category._id)
    .filter(id => {
      let current: string | null | undefined = id;
      const visited = new Set<string>();
      while (current && !visited.has(current)) {
        if (current === mainCategoryId) return true;
        visited.add(current);
        current = parents.get(current) || null;
      }
      return false;
    });
  return ids.length > 0 ? ids : [mainCategoryId];
};
